import { createContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import type { UserRole } from '@wanderlust/shared';
import { api, ApiError, broadcastTokensToExtension } from '../lib/api';
import { safeStorage } from '../lib/safeStorage';

interface User {
  id: string;
  email: string;
  full_name?: string;
  role?: UserRole;
}

export interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName?: string, inviteToken?: string) => Promise<void>;
  signOut: () => Promise<void>;
  setTokens: (accessToken: string, refreshToken: string) => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const EXPIRES_AT_KEY = 'expires_at';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const clearAuth = useCallback(() => {
    safeStorage.removeItem(TOKEN_KEY);
    safeStorage.removeItem(REFRESH_TOKEN_KEY);
    safeStorage.removeItem(EXPIRES_AT_KEY);
    setUser(null);
  }, []);

  const setAuthData = useCallback((data: {
    user: { id: string; email: string; full_name?: string; role?: UserRole };
    access_token: string;
    refresh_token: string;
    expires_at: number;
  }) => {
    safeStorage.setItem(TOKEN_KEY, data.access_token);
    safeStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
    safeStorage.setItem(EXPIRES_AT_KEY, data.expires_at.toString());
    setUser({
      id: data.user.id,
      email: data.user.email,
      full_name: data.user.full_name,
      role: data.user.role,
    });

    // Broadcast tokens to browser extension
    broadcastTokensToExtension({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      expires_at: data.expires_at,
    });
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = safeStorage.getItem(TOKEN_KEY);
      const expiresAt = safeStorage.getItem(EXPIRES_AT_KEY);

      if (!token) {
        setLoading(false);
        return;
      }

      // Check if token is expired
      if (expiresAt && Date.now() / 1000 > parseInt(expiresAt, 10)) {
        // Try to refresh
        const refreshToken = safeStorage.getItem(REFRESH_TOKEN_KEY);
        if (refreshToken) {
          try {
            const data = await api.refreshToken(refreshToken);
            setAuthData(data);
            setLoading(false);
            return;
          } catch {
            clearAuth();
            setLoading(false);
            return;
          }
        }
        clearAuth();
        setLoading(false);
        return;
      }

      // Token exists and not expired, verify it
      try {
        const { user: userData } = await api.getMe();
        setUser({
          id: userData.id,
          email: userData.email,
          role: userData.role,
          full_name: userData.fullName,
        });
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          // Try to refresh
          const refreshToken = safeStorage.getItem(REFRESH_TOKEN_KEY);
          if (refreshToken) {
            try {
              const data = await api.refreshToken(refreshToken);
              setAuthData(data);
              setLoading(false);
              return;
            } catch {
              clearAuth();
            }
          } else {
            clearAuth();
          }
        }
      }

      setLoading(false);
    };

    checkAuth();
  }, [clearAuth, setAuthData]);

  // Remove the prerender hint class once React owns the auth UI
  useEffect(() => {
    if (!loading) {
      document.documentElement.classList.remove('has-token');
    }
  }, [loading]);

  const signIn = async (email: string, password: string) => {
    const data = await api.signIn({ email, password });
    setAuthData(data);
  };

  const signUp = async (email: string, password: string, fullName?: string, inviteToken?: string) => {
    const data = await api.signUp({ email, password, full_name: fullName, invite_token: inviteToken });
    // If email confirmation is required, data won't have tokens
    if ('access_token' in data) {
      setAuthData(data);
    }
  };

  const signOut = async () => {
    try {
      await api.signOut();
    } catch {
      // Revoking the session server-side is best effort. An expired access token
      // (401 "Invalid or expired token") or a dropped connection still leaves the
      // user signed out locally, and every caller navigates away right after, so
      // rethrowing here would only skip that redirect and surface a bogus error.
    } finally {
      clearAuth();
    }
  };

  const setTokens = async (accessToken: string, refreshToken: string) => {
    safeStorage.setItem(TOKEN_KEY, accessToken);
    safeStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    // Fetch user data with the new token
    try {
      const { user: userData } = await api.getMe();
      setUser({
        id: userData.id,
        email: userData.email,
        role: userData.role,
        full_name: userData.fullName,
      });
    } catch {
      clearAuth();
      throw new Error('Failed to verify token');
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signOut, setTokens }}>
      {children}
    </AuthContext.Provider>
  );
}
